import { MAIL_FONTS, type MailLayoutInput } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Braces, Lock, Mail, Palette, RotateCcw, Send } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { RichEditor, type EditorVariable } from '@/components/rich-editor';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Select, Skeleton, Switch } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

interface TemplateItem {
  key: string;
  label: string;
  group: string;
  description: string;
  customized: boolean;
  active: boolean;
  /** wird versendet */
  enabled: boolean;
  /** Zugangs- und Code-Mails lassen sich nicht abschalten */
  canDisable: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}
interface Layout extends Omit<MailLayoutInput, 'primaryColor'> {
  primaryColor: string | null;
}
interface TemplateDetail {
  key: string;
  label: string;
  description: string;
  customized: boolean;
  active: boolean;
  subject: string;
  bodyHtml: string;
  defaults: { subject: string; bodyHtml: string };
  variables: (EditorVariable & { block: boolean })[];
  required: string[];
  requiredInSubject: string[];
}

const onError = (e: unknown) => toast.error(errorMessage(e));

/** Vorschau in einem abgeschotteten Rahmen (keine Skripte, kein Zugriff auf die Seite) */
function PreviewFrame({ html, subject, loading }: { html: string | undefined; subject?: string; loading?: boolean }) {
  return (
    <div className="grid gap-2">
      {subject !== undefined && (
        <p className="truncate rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span className="text-muted-foreground">Betreff:</span> {subject}
        </p>
      )}
      <div className="relative overflow-hidden rounded-md border bg-white">
        {loading && <span className="absolute right-2 top-2 rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">aktualisiere …</span>}
        {html ? <iframe title="Vorschau der E-Mail" sandbox="" srcDoc={html} className="h-[38rem] w-full" /> : <Skeleton className="h-[38rem]" />}
      </div>
      <p className="text-xs text-muted-foreground">Vorschau mit Beispielwerten.</p>
    </div>
  );
}

function useDebounced<T>(value: T, ms = 600) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function TemplateEditor({ templateKey, onBack }: { templateKey: string; onBack: () => void }) {
  const qc = useQueryClient();
  const [confirmDialog, confirm] = useConfirm();
  const { data } = useQuery({ queryKey: ['mail-templates', templateKey], queryFn: () => api<TemplateDetail>(`/mail-templates/${templateKey}`) });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [active, setActive] = useState(true);
  const subjectRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (data) {
      setSubject(data.subject);
      setBody(data.bodyHtml);
      setActive(data.active);
    }
  }, [data]);
  const preview = useQuery({
    queryKey: ['mail-templates', 'preview', templateKey, useDebounced(subject), useDebounced(body)],
    queryFn: () => api<{ subject: string; html: string }>('/mail-templates/preview', { method: 'POST', body: { key: templateKey, subject, bodyHtml: body } }),
    enabled: Boolean(data && body),
    placeholderData: (p) => p,
    retry: false,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['mail-templates'] });
  const save = useMutation({
    mutationFn: () => api(`/mail-templates/${templateKey}`, { method: 'PUT', body: { subject, bodyHtml: body, active } }),
    onSuccess: () => {
      toast.success('Vorlage gespeichert – gilt ab sofort für neue Mails');
      refresh();
    },
    onError,
  });
  const reset = useMutation({
    mutationFn: () => api(`/mail-templates/${templateKey}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Standardtext wiederhergestellt');
      refresh();
    },
    onError,
  });
  const test = useMutation({
    mutationFn: () => api<{ to: string }>('/mail-templates/test', { method: 'POST', body: { key: templateKey, subject, bodyHtml: body } }),
    onSuccess: (r) => toast.success(`Testmail an ${r.to} gesendet`),
    onError,
  });
  const textVars = useMemo(() => data?.variables.filter((v) => !v.block) ?? [], [data]);
  if (!data) return <Skeleton className="h-96" />;

  const insertInSubject = (key: string) => {
    const el = subjectRef.current;
    const pos = el?.selectionStart ?? subject.length;
    setSubject(`${subject.slice(0, pos)}{{${key}}}${subject.slice(pos)}`);
  };
  const missing = [...data.required.filter((r) => !(r === 'link' ? /\{\{\s*(link|schaltflaeche)\s*\}\}/.test(body + subject) : body.includes(`{{${r}}}`) || subject.includes(`{{${r}}}`))), ...data.requiredInSubject.filter((r) => !subject.includes(`{{${r}}}`))];

  return (
    <div className="grid gap-4">
      {confirmDialog}
      <div>
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> E-Mail-Vorlagen
        </button>
      </div>
      <PageHeader
        title={data.label}
        description={data.description}
        actions={
          <div className="flex flex-wrap gap-2">
            {data.customized && (
              <Button
                variant="ghost"
                onClick={async () => (await confirm({ title: 'Auf Standardtext zurücksetzen?', description: 'Ihre Anpassungen an dieser Vorlage werden gelöscht.', confirmLabel: 'Zurücksetzen', destructive: true })) && reset.mutate()}
              >
                <RotateCcw /> Standard
              </Button>
            )}
            <Button variant="outline" onClick={() => test.mutate()} loading={test.isPending}>
              <Send /> Testmail an mich
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={missing.length > 0}>
              Speichern
            </Button>
          </div>
        }
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardContent className="grid gap-3 pt-5">
            <Field label="Betreff" htmlFor="tpl-subject">
              <div className="flex gap-2">
                <Input id="tpl-subject" ref={subjectRef} value={subject} onChange={(e) => setSubject(e.target.value)} />
                <Select aria-label="Variable in den Betreff einfügen" value="" onChange={(e) => e.target.value && insertInSubject(e.target.value)} className="w-40 shrink-0">
                  <option value="">+ Variable</option>
                  {textVars.map((v) => (
                    <option key={v.key} value={v.key}>
                      {v.label}
                    </option>
                  ))}
                </Select>
              </div>
            </Field>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">Text</span>
              <RichEditor value={body} onChange={setBody} variables={data.variables} ariaLabel="Text der E-Mail" minHeight="22rem" />
            </div>
            {missing.length > 0 && (
              <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
                Erforderlich: {missing.map((m) => `{{${m === 'link' ? 'schaltflaeche' : m}}}`).join(', ')}
                {data.requiredInSubject.length > 0 && ' – die Ticketnummer im Betreff ordnet Antworten per E-Mail dem Vorgang zu.'}
              </p>
            )}
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground">
                <Braces className="mr-1 inline size-4" /> Was bedeuten die Variablen?
              </summary>
              <ul className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
                {data.variables.map((v) => (
                  <li key={v.key}>
                    <code className="text-primary">{`{{${v.key}}}`}</code> – {v.label}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">
                {'{{schaltflaeche}}, {{nachricht}}, {{standardtext}} und {{signatur}} sind Bausteine – am besten jeweils in eine eigene Zeile setzen. Die Signatur legen Sie unter „Design“ fest; fehlt {{signatur}}, wird sie unten angefügt.'}
              </p>
            </details>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={active} onCheckedChange={setActive} /> Eigene Vorlage verwenden (aus = Standardtext)
            </label>
          </CardContent>
        </Card>
        <PreviewFrame html={preview.data?.html} subject={preview.data?.subject} loading={preview.isFetching} />
      </div>
    </div>
  );
}

function LayoutEditor({ initial, onBack }: { initial: Layout; onBack: () => void }) {
  const qc = useQueryClient();
  const [l, setL] = useState<Layout>(initial);
  const set = <K extends keyof Layout>(k: K, v: Layout[K]) => setL((x) => ({ ...x, [k]: v }));
  const debounced = useDebounced(l);
  const preview = useQuery({
    queryKey: ['mail-templates', 'layout-preview', debounced],
    queryFn: () => api<{ html: string }>('/mail-templates/preview', { method: 'POST', body: { key: 'ticket-reply', layout: { ...debounced, primaryColor: debounced.primaryColor || null } } }),
    placeholderData: (p) => p,
  });
  const save = useMutation({
    mutationFn: () => api('/mail-templates/layout', { method: 'PUT', body: { ...l, primaryColor: l.primaryColor || null } }),
    onSuccess: () => {
      toast.success('Design gespeichert – gilt für alle E-Mails');
      qc.invalidateQueries({ queryKey: ['mail-templates'] });
    },
    onError,
  });
  const vars: EditorVariable[] = [
    { key: 'firma.name', label: 'Ihr Unternehmen', group: 'Allgemein' },
    { key: 'firma.telefon', label: 'Telefon', group: 'Allgemein' },
    { key: 'firma.email', label: 'E-Mail', group: 'Allgemein' },
    { key: 'firma.adresse', label: 'Adresse', group: 'Allgemein' },
    { key: 'absender.name', label: 'Mitarbeiter, der schreibt', group: 'Mitarbeiter' },
    { key: 'mitarbeiter.name', label: 'Zuständiger Mitarbeiter', group: 'Mitarbeiter' },
  ];
  return (
    <div className="grid gap-4">
      <div>
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> E-Mail-Vorlagen
        </button>
      </div>
      <PageHeader
        title="Design aller E-Mails"
        description="Farben, Schrift, Logo, Kopf- und Fußzeile und die Signatur gelten für alle Mails des Portals – auch für die Standardtexte."
        actions={
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Speichern
          </Button>
        }
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardContent className="grid gap-4 pt-5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(
                [
                  ['primaryColor', 'Akzentfarbe'],
                  ['backgroundColor', 'Hintergrund'],
                  ['textColor', 'Schriftfarbe'],
                ] as const
              ).map(([k, label]) => (
                <Field key={k} label={label} htmlFor={`l-${k}`}>
                  <Input id={`l-${k}`} type="color" value={l[k] ?? '#1d4ed8'} onChange={(e) => set(k, e.target.value)} className="h-9 p-1" />
                </Field>
              ))}
              <Field label="Schrift" htmlFor="l-font">
                <Select id="l-font" value={l.fontFamily} onChange={(e) => set('fontFamily', e.target.value as Layout['fontFamily'])}>
                  {Object.entries(MAIL_FONTS).map(([k, f]) => (
                    <option key={k} value={k}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {l.primaryColor && (
              <button type="button" className="justify-self-start text-xs text-muted-foreground hover:text-foreground" onClick={() => set('primaryColor', null)}>
                Akzentfarbe wie Branding (Unternehmen)
              </button>
            )}
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={l.showLogo} onCheckedChange={(v) => set('showLogo', v)} /> Logo oben anzeigen (aus Administration → Unternehmen)
            </label>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">Signatur</span>
              <RichEditor value={l.signatureHtml} onChange={(v) => set('signatureHtml', v)} variables={vars} minHeight="8rem" ariaLabel="Signatur" />
            </div>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">Kopfzeile (unter dem Logo, optional)</span>
              <RichEditor value={l.headerHtml} onChange={(v) => set('headerHtml', v)} variables={vars} minHeight="4rem" ariaLabel="Kopfzeile" />
            </div>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">Fußzeile (z. B. Impressum, Kontakt)</span>
              <RichEditor value={l.footerHtml} onChange={(v) => set('footerHtml', v)} variables={vars} minHeight="6rem" ariaLabel="Fußzeile" />
            </div>
          </CardContent>
        </Card>
        <PreviewFrame html={preview.data?.html} loading={preview.isFetching} />
      </div>
    </div>
  );
}

export function MailTemplatesPage() {
  const [editing, setEditing] = useState<string | null>(null);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const { data } = useQuery({ queryKey: ['mail-templates'], queryFn: () => api<{ templates: TemplateItem[]; layout: Layout }>('/mail-templates') });
  const listQc = useQueryClient();
  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) => api(`/mail-templates/${key}/enabled`, { method: 'PUT', body: { enabled } }),
    onSuccess: (_d, v) => {
      toast.success(v.enabled ? 'Wird wieder versendet' : 'Wird nicht mehr versendet');
      listQc.invalidateQueries({ queryKey: ['mail-templates'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (editing) return <TemplateEditor templateKey={editing} onBack={() => setEditing(null)} />;
  if (layoutOpen && data) return <LayoutEditor initial={data.layout} onBack={() => setLayoutOpen(false)} />;
  const groups = [...new Set(data?.templates.map((t) => t.group) ?? [])];
  return (
    <div className="grid gap-4">
      <PageHeader
        title="E-Mail-Vorlagen"
        description="Texte aller E-Mails anpassen – mit Variablen wie Kunden-, Ansprechpartner- oder Mitarbeitername, Bildern und Signatur. Ohne Anpassung gilt der Standardtext. Mit dem Schalter rechts lässt sich der Versand je E-Mail-Art abschalten."
        actions={
          <Button variant="outline" onClick={() => setLayoutOpen(true)} disabled={!data}>
            <Palette /> Design und Signatur
          </Button>
        }
      />
      {!data && <Skeleton className="h-96" />}
      <div className="grid gap-4 lg:grid-cols-2 3xl:grid-cols-3">
        {groups.map((g) => (
          <Card key={g}>
            <CardHeader>
              <CardTitle className="text-base">{g}</CardTitle>
              {g === 'Intern' && <CardDescription>Benachrichtigungen an Mitarbeitende</CardDescription>}
            </CardHeader>
            <CardContent className="grid gap-1">
              {data!.templates
                .filter((t) => t.group === g)
                .map((t) => (
                  <div key={t.key} className={`flex items-start gap-2 rounded-md hover:bg-accent ${t.enabled ? '' : 'opacity-60'}`}>
                  <button type="button" onClick={() => setEditing(t.key)} className="flex min-w-0 flex-1 items-start justify-between gap-3 px-2 py-2 text-left">
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Mail className="size-4 shrink-0 text-muted-foreground" /> {t.label}
                        {!t.enabled && <Badge variant="outline">wird nicht versendet</Badge>}
                      </span>
                      <span className="block text-xs text-muted-foreground">{t.description}</span>
                      {t.customized && t.updatedAt && (
                        <span className="block text-xs text-muted-foreground">
                          geändert {formatDateTime(t.updatedAt)}
                          {t.updatedByName ? ` von ${t.updatedByName}` : ''}
                        </span>
                      )}
                    </span>
                    {t.customized ? t.active ? <Badge>angepasst</Badge> : <Badge variant="outline">angepasst, aus</Badge> : <Badge variant="outline">Standard</Badge>}
                  </button>
                  <span className="shrink-0 py-2 pr-2" title={t.canDisable ? (t.enabled ? 'Versand abschalten' : 'Versand einschalten') : 'Zugangs- und Code-Mails werden immer versendet'}>
                    {t.canDisable ? (
                      <Switch checked={t.enabled} disabled={toggle.isPending} onCheckedChange={(v) => toggle.mutate({ key: t.key, enabled: v })} aria-label={`${t.label} versenden`} />
                    ) : (
                      <Lock className="m-1 size-4 text-muted-foreground" aria-label="immer aktiv" />
                    )}
                  </span>
                  </div>
                ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
